import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebounceValue } from '../../shared/hooks/useDebounceValue';
import { rememberListUrl } from './listReturn';

const RESERVED = ['page', 'perPage', 'search', 'sort', 'dir'];

/**
 * Состояние списка админки: страница, поиск, сортировка, фильтры + загрузка данных.
 *
 * Состояние хранится в адресе (?page=3&search=нефть&view=0): после перехода в карточку
 * и обратно, обновления страницы или по присланной ссылке список открывается там же,
 * где его оставили. В адрес попадают только значения, отличные от значений по умолчанию.
 *
 * @param fetcher (params) => Promise<{items,total,page,perPage,lastPage}>
 */
export const useAdminList = (fetcher, { perPage: defaultPerPage = 20, sort: defaultSort = 'id', dir: defaultDir = 'desc' } = {}) => {
    const history = useHistory();
    const location = useLocation();

    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const page = Math.max(1, parseInt(query.get('page'), 10) || 1);
    const perPage = parseInt(query.get('perPage'), 10) || defaultPerPage;
    const sort = query.get('sort') || defaultSort;
    const dir = query.get('dir') || defaultDir;
    const urlSearch = query.get('search') || '';

    // Фильтры — все прочие параметры адреса. Объект пересобирается только при смене
    // адреса, чтобы не запускать лишние загрузки.
    const filters = useMemo(() => {
        const result = {};
        query.forEach((value, key) => {
            if (!RESERVED.includes(key)) result[key] = value;
        });
        return result;
    }, [query]);

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ total: 0, lastPage: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    // Поле поиска откликается сразу, а в адрес (и в запрос) значение уходит с задержкой.
    const [search, setSearchInput] = useState(urlSearch);
    const debouncedSearch = useDebounceValue(search, 350);

    // Переход «Назад»/«Вперёд» в браузере меняет адрес — поле поиска следует за ним.
    const lastUrlSearch = useRef(urlSearch);
    useEffect(() => {
        if (urlSearch !== lastUrlSearch.current) {
            lastUrlSearch.current = urlSearch;
            setSearchInput(urlSearch);
        }
    }, [urlSearch]);

    /** Меняет параметры адреса; значения по умолчанию из адреса убираются. */
    const update = useCallback(
        (changes, { resetPage = true } = {}) => {
            const params = new URLSearchParams(location.search);
            const defaults = { page: '1', perPage: String(defaultPerPage), sort: defaultSort, dir: defaultDir, search: '' };
            const next = resetPage ? { page: 1, ...changes } : changes;

            Object.entries(next).forEach(([key, value]) => {
                const str = value === null || value === undefined ? '' : String(value);
                if (str === '' || defaults[key] === str) {
                    params.delete(key);
                } else {
                    params.set(key, str);
                }
            });

            const search = params.toString();
            const target = search ? `?${search}` : '';
            if (target !== location.search) {
                history.replace({ pathname: location.pathname, search: target });
            }
        },
        [history, location.pathname, location.search, defaultPerPage, defaultSort, defaultDir]
    );

    useEffect(() => {
        const value = debouncedSearch.trim();
        if (value !== urlSearch) {
            lastUrlSearch.current = value;
            update({ search: value });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    // Запоминаем адрес списка, чтобы «← К списку» в карточке возвращал сюда же.
    useEffect(() => {
        rememberListUrl(location.pathname, location.search);
    }, [location.pathname, location.search]);

    // Загрузчик держим в ref, чтобы инлайновая стрелочная функция на стороне страницы
    // не перезапускала эффект на каждый рендер. Если у страницы меняется источник
    // данных (DictionaryPage), её нужно пересоздавать через key.
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const params = { page, perPage, sort, dir, ...filters };
        if (urlSearch.trim()) params.search = urlSearch.trim();

        fetcherRef
            .current(params)
            .then((data) => {
                if (cancelled) return;
                setRows(data.items || []);
                setMeta({ total: data.total || 0, lastPage: data.lastPage || 1 });
            })
            .catch((err) => {
                if (cancelled) return;
                setRows([]);
                setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, perPage, sort, dir, urlSearch, filters, reloadToken]);

    const reload = useCallback(() => setReloadToken((t) => t + 1), []);

    const setPage = useCallback((value) => update({ page: value }, { resetPage: false }), [update]);

    // Любое изменение условий выборки возвращает на первую страницу:
    // иначе после поиска можно оказаться на несуществующей десятой странице.
    const setSearch = useCallback((value) => setSearchInput(value), []);

    const setPerPage = useCallback((value) => update({ perPage: value }), [update]);

    const toggleSort = useCallback(
        (column) => {
            if (sort === column) {
                update({ sort: column, dir: dir === 'asc' ? 'desc' : 'asc' });
            } else {
                update({ sort: column, dir: 'asc' });
            }
        },
        [sort, dir, update]
    );

    const setFilter = useCallback((key, value) => update({ [key]: value }), [update]);
    /** Несколько фильтров разом (например, журнал и сброс выпуска). */
    const setFilters = useCallback((changes) => update(changes), [update]);

    return {
        rows,
        total: meta.total,
        lastPage: meta.lastPage,
        loading,
        error,
        page,
        setPage,
        perPage,
        setPerPage,
        search,
        setSearch,
        sort,
        dir,
        toggleSort,
        filters,
        setFilter,
        setFilters,
        reload,
    };
};

export default useAdminList;
